<template>
  <HeaderDefault></HeaderDefault>
  <div class="min-h-screen bg-gray-100">
    <section class="max-w-screen-2xl mx-auto px-4 py-8 sm:py-12">
      <!-- Mobile Controls Bar -->
      <div class="flex items-center justify-between mb-6 md:hidden">
        <div class="flex items-center gap-3">
          <EventsLayoutButton
            icon="heroicons:squares-2x2"
            :active="layout === 'grid'"
            @click="layout = 'grid'"
          />
          <EventsLayoutButton
            icon="heroicons:bars-3"
            :active="layout === 'list'"
            @click="layout = 'list'"
          />
        </div>
        <select
          v-model="sortBy"
          @change="updateFilters"
          class="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="date_desc">Newest First</option>
          <option value="date_asc">Oldest First</option>
          <option value="title_asc">Title A-Z</option>
          <option value="title_desc">Title Z-A</option>
        </select>
      </div>

      <!-- Active Filters Display (Mobile) -->
      <div v-if="activeFilters.length" class="md:hidden mb-4 px-1">
        <div class="flex flex-wrap gap-2">
          <EventsFilterButton
            v-for="categoryId in selectedCategories"
            :key="`cat-${categoryId}`"
            :label="getCategoryName(categoryId)"
            @click="toggleCategory(categoryId)"
            small
          />
          <EventsFilterButton
            v-for="tagId in selectedTags"
            :key="`tag-${tagId}`"
            :label="getTagName(tagId)"
            @click="toggleTag(tagId)"
            small
          />
          <EventsFilterButton
            v-for="typeId in selectedTypes"
            :key="`type-${typeId}`"
            :label="getTypeName(typeId)"
            @click="toggleType(typeId)"
            small
          />
        </div>
      </div>

      <!-- Main Content -->
      <div class="flex flex-col sm:flex-row justify-between items-start gap-6">
        <!-- Sidebar - 1/4 width -->
        <EventsFilters
          v-model:selectedCategories="selectedCategories"
          v-model:selectedTags="selectedTags"
          v-model:selectedTypes="selectedTypes"
          v-model:showFeatured="showFeatured"
          v-model:showStreams="showStreams"
          v-model:search="searchQuery"
          :categories="categories"
          :tags="tags"
          :types="types"
          @filter="updateFilters"
        />

        <!-- Right side content - remaining width -->
        <div class="w-full sm:p-5 rounded mb-5">
          <!-- Layout and Sort options -->
          <div class="hidden sm:flex justify-end items-start mb-6 gap-5">
            <!-- Mobile Filter Summary -->
            <div class="flex-1 max-w-[100%]">
              <div class="mb-0">
                <div class="flex items-center justify-between">
                  <h3 class="text-sm font-medium text-gray-700">Filters</h3>
                </div>
                <div v-if="activeFilters.length" class="flex flex-wrap gap-2">
                  <EventsFilterButton
                    v-for="categoryId in selectedCategories"
                    :key="`cat-${categoryId}`"
                    :label="getCategoryName(categoryId)"
                    @click="toggleCategory(categoryId)"
                  />
                  <EventsFilterButton
                    v-for="tagId in selectedTags"
                    :key="`tag-${tagId}`"
                    :label="getTagName(tagId)"
                    @click="toggleTag(tagId)"
                  />
                  <EventsFilterButton
                    v-for="typeId in selectedTypes"
                    :key="`type-${typeId}`"
                    :label="getTypeName(typeId)"
                    @click="toggleType(typeId)"
                  />
                  <EventsFilterButton
                    v-if="showFeatured"
                    label="Featured Events"
                    @click="toggleFeatured"
                  />
                  <EventsFilterButton
                    v-if="showStreams"
                    label="Streams Only"
                    @click="toggleStreams"
                  />
                </div>

                <div v-else class="text-sm text-gray-500">
                  Showing all events
                </div>
              </div>
            </div>

            <!-- Controls section -->
            <div class="flex items-center gap-4 shrink-0">
              <EventsLayoutButton
                icon="heroicons:squares-2x2"
                :active="layout === 'grid'"
                @click="layout = 'grid'"
              />
              <EventsLayoutButton
                icon="heroicons:bars-3"
                :active="layout === 'list'"
                @click="layout = 'list'"
              />
            </div>
            <select
              v-model="sortBy"
              @change="updateFilters"
              class="bg-white dark:bg-primary/10 border border-gray-300 rounded-lg px-4 py-2 text-sm"
            >
              <option value="date_desc">Newest First</option>
              <option value="date_asc">Oldest First</option>
              <option value="title_asc">Title A-Z</option>
              <option value="title_desc">Title Z-A</option>
            </select>
          </div>

          <!-- Events Grid/List -->
          <div :class="layoutClass">
            <template v-if="isLoading">
              <EventsSkeletonLoader v-for="n in perPage" :key="n" />
            </template>
            <EventsCard
              v-else
              v-for="event in filteredEvents"
              :key="event.id"
              :event="event"
              :list-view="layout === 'list'"
            />
          </div>
          <!-- {{ filteredEvents[0].attributes.aa_events_type.data.attributes.title }} -->
          <!-- No Results Message -->
          <EventsNoResultMessage
            v-if="!isLoading && filteredEvents.length === 0"
            :activeFilters="activeFilters"
            @resetFilters="resetFilters"
          />

          <!-- Pagination -->
          <div class="mt-8 flex justify-center">
            <EventsPagination
              v-model="currentPage"
              :total="pagination.pageCount"
              :per-page="1"
              @update:model-value="updateFilters"
            />
          </div>
        </div>
      </div>
    </section>
  </div>
  <UBanner></UBanner>
  <FooterDefault></FooterDefault>
</template>

<script setup lang="ts">
const isLoading = ref(false);
const searchQuery = ref("");
const route = useRoute();
const router = useRouter();
const store = useEventStore();

// UI States
const layout = ref("list");
const currentPage = ref(1);
const perPage = ref(12);

// Filter States
const selectedCategories = ref([]);
const selectedTags = ref([]);
const selectedTypes = ref([]);
const showFeatured = ref(false);
const showStreams = ref(false);
const sortBy = ref("date_desc");

// Data States
const categories = ref([]);
const tags = ref([]);
const types = ref([]);
const events = ref([]);
const pagination = ref({
  page: 1,
  pageSize: 12,
  pageCount: 1,
  total: 0,
});

// Computed Properties
const filteredEvents = computed(() => events.value || []);
const layoutClass = computed(() =>
  layout.value === "grid"
    ? "grid grid-cols-1 md:grid-cols-2 gap-6"
    : "space-y-4"
);
// In the script section, add these computed properties and methods:
const activeFilters = computed(() => {
  const filters = [];
  if (selectedCategories.value.length)
    filters.push(`${selectedCategories.value.length} categories`);
  if (selectedTags.value.length)
    filters.push(`${selectedTags.value.length} tags`);
  if (selectedTypes.value.length)
    filters.push(`${selectedTypes.value.length} types`);
  if (showFeatured.value) filters.push("featured events");
  if (showStreams.value) filters.push("streams only");
  return filters;
});

// Add new helper method for types
const getTypeName = (id) =>
  types.value.find((t) => t.id === id)?.attributes?.title || "Unknown Type";

const selectType = (typeId) => {
  selectedTypes.value = selectedTypes.value.includes(typeId) ? [] : [typeId];
  updateFilters();
};

const toggleType = (typeId) => {
  selectedTypes.value = selectedTypes.value.filter((id) => id !== typeId);
  updateFilters();
};

// Methods

const debounce = (fn: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      fn(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const handleSearch = debounce(() => {
  currentPage.value = 1; // Reset to first page when searching
  updateFilters();
}, 300); // 300ms delay

const fetchEvents = async () => {
  isLoading.value = true;
  try {
    const sortMapping = {
      date_desc: "starts:desc",
      date_asc: "starts:asc",
      title_desc: "title:desc",
      title_asc: "title:asc",
    };

    const response = await store.getEvents({
      page: currentPage.value,
      pageSize: perPage.value,
      categories: selectedCategories.value,
      tags: selectedTags.value,
      types: selectedTypes.value,
      featured: showFeatured.value,
      streams: showStreams.value,
      sort: sortMapping[sortBy.value],
      search: searchQuery.value,
    });

    events.value = response.data;
    pagination.value = response.meta.pagination;
  } finally {
    isLoading.value = false;
  }
};

const initializeFromUrl = () => {
  const query = route.query;
  selectedCategories.value = Array.isArray(query.categories)
    ? query.categories.map(Number)
    : query.categories
    ? [Number(query.categories)]
    : [];
  selectedTags.value = Array.isArray(query.tags)
    ? query.tags.map(Number)
    : query.tags
    ? [Number(query.tags)]
    : [];
  selectedTypes.value = Array.isArray(query.types)
    ? query.types.map(Number)
    : query.types
    ? [Number(query.types)]
    : [];
  showFeatured.value = query.featured === "true";
  showStreams.value = query.streams === "true";
  sortBy.value = query.sort?.toString() || "date_desc";
  currentPage.value = Number(query.page) || 1;
  layout.value = query.layout?.toString() === "list" ? "list" : "grid";
  searchQuery.value = query.search?.toString() || "";
};

const updateFilters = async () => {
  const query = {
    categories: selectedCategories.value.length
      ? selectedCategories.value
      : undefined,
    tags: selectedTags.value.length ? selectedTags.value : undefined,
    types: selectedTypes.value.length ? selectedTypes.value : undefined,
    featured: showFeatured.value || undefined,
    streams: showStreams.value || undefined,
    sort: sortBy.value,
    page: currentPage.value,
    layout: layout.value,
    search: searchQuery.value || undefined,
  };

  router.push({ query });
  await fetchEvents();
};

const resetFilters = async () => {
  selectedCategories.value = [];
  selectedTags.value = [];
  selectedTypes.value = [];
  showFeatured.value = false;
  showStreams.value = false;
  sortBy.value = "date_desc";
  currentPage.value = 1;
  layout.value = "list";
  searchQuery.value = "";
  await updateFilters();
};

const handleTitleSearch = (value) => {
  searchQuery.value = value;
  updateFilters();
};

const getCategoryName = (id) =>
  categories.value.find((c) => c.id === id)?.attributes?.title ||
  "Unknown Category";
const getTagName = (id) =>
  tags.value.find((t) => t.id === id)?.attributes?.title || "Unknown Tag";
const selectCategory = (categoryId) => {
  selectedCategories.value = selectedCategories.value.includes(categoryId)
    ? []
    : [categoryId];
  updateFilters();
};
const toggleCategory = (categoryId) => {
  selectedCategories.value = selectedCategories.value.filter(
    (id) => id !== categoryId
  );
  updateFilters();
};
const toggleTag = (tagId) => {
  selectedTags.value = selectedTags.value.filter((id) => id !== tagId);
  updateFilters();
};
const toggleFeatured = () => {
  showFeatured.value = false;
  updateFilters();
};
const toggleStreams = () => {
  showStreams.value = false;
  updateFilters();
};

// Lifecycle Hooks
onMounted(async () => {
  isLoading.value = true;
  initializeFromUrl();
  categories.value = await store.getCategories();
  tags.value = await store.getTags();
  types.value = await store.getEventTypes();
  await fetchEvents();
});

// Watchers
watch(
  () => route.query,
  async () => {
    await initializeFromUrl();
    await fetchEvents();
  },
  { deep: true }
);
</script>

<style scoped>
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #e5e7eb;
  border-radius: 20px;
}
</style>
